import type { CSSProperties } from 'react';
import type { SectionDesign } from '../model/section-design';
import type { StyleClasses } from './styles';

export const SECTION_MAX_WIDTH = { narrow: '42rem', standard: '64rem', wide: '80rem', full: 'none' } as const;

/** Only explicit color slots receive these classes. Embedded form fields,
 *  buttons and canvas chrome never match a broad descendant color selector. */
export function sectionDesignClasses(s: StyleClasses, design?: SectionDesign): StyleClasses {
  if (!design?.text_color && !design?.heading_color) return s;
  const text = design.text_color ? ' lp-design-text' : '';
  return {
    ...s,
    heading: s.heading + (design.heading_color ? ' lp-design-heading' : ''),
    sub: s.sub + text,
    ...(s.label ? { label: s.label + text } : {}),
    ...(s.eyebrow ? { eyebrow: s.eyebrow + text } : {}),
  };
}

/** CSS media queries run in the actual rendered document, including the
 *  editor iframe. `stack` also makes the editor's Phone preview deterministic. */
export function sectionDesignPresentation(design?: SectionDesign, stack?: boolean): { className: string; style?: CSSProperties } {
  if (!design) return { className: '' };
  const style: Record<string, string | number> = {};
  const classes = ['lp-section-design'];
  if (design.background_color) { style.backgroundColor = design.background_color; style['--band-bg'] = design.background_color; }
  if (design.text_color) { style.color = design.text_color; style['--section-text'] = design.text_color; }
  if (design.heading_color) style['--section-heading'] = design.heading_color;
  if (design.content_width) style['--section-width'] = SECTION_MAX_WIDTH[design.content_width];
  for (const edge of ['top', 'bottom'] as const) {
    const desktop = design[`padding_${edge}`];
    const mobile = design[`mobile_padding_${edge}`];
    if (desktop !== undefined) { classes.push(`lp-design-${edge}`); style[`--section-${edge}`] = `${desktop}px`; }
    if (mobile !== undefined) {
      classes.push(`lp-design-mobile-${edge}`);
      style[`--section-mobile-${edge}`] = `${mobile}px`;
      if (stack) style[edge === 'top' ? 'paddingTop' : 'paddingBottom'] = mobile;
    }
  }
  return { className: ` ${classes.join(' ')}`, style };
}

/** Shared only by collection roots, never their cards or nested form grids. */
export function sectionGridProps(design?: SectionDesign, stack?: boolean, layout?: 'timeline' | 'module-rows' | 'review-list' | 'review-wall') {
  if (!design || (design.columns === undefined && design.mobile_columns === undefined)) return {};
  const mobile = design.mobile_columns;
  const phoneOverride = !!stack && mobile !== undefined;
  return {
    'data-section-grid': true,
    'data-section-grid-columns': stack ? undefined : design.columns,
    'data-section-grid-mobile': mobile,
    'data-section-grid-stack': phoneOverride || undefined,
    'data-section-grid-layout': layout,
    style: {
      '--section-columns': design.columns,
      '--section-mobile-columns': mobile,
      ...(phoneOverride ? { display: 'grid', maxWidth: 'none', gridTemplateColumns: `repeat(${mobile}, minmax(0, 1fr))` } : {}),
    } as CSSProperties,
  };
}
